import { useTranslation } from 'react-i18next';
import { Alert } from '@/kit/Alert';
import { useGetNextRepayment } from '@/query/queries/cash/useGetNextRepayment';
import { useGetFinances } from '@/query/queries/cash/useGetFinances';
import IconWarn from '@/pages/PaymentsNextPage/assets/icon_warn.svg';
import IconDanger from '@/pages/PaymentsNextPage/assets/icon_danger.svg';
import Preloader from '@/components/UI/Preloader';
import { ACCOUNT_TYPES } from '@/helpers/constants';
import { prettyFloatMoney } from '@/helpers/utils';
import { prettyDate } from '@/helpers/date';
import IconCalendar from './icon_calendar.svg';
import IconCoins from './icon_coins.svg';
import { Table } from '../Table';
import s from './index.module.scss';

export const NextRepayment = () => {
  const { data, isLoading } = useGetNextRepayment();
  const { data: finances } = useGetFinances();
  const { t } = useTranslation('next-repayment');

  if (isLoading) return <Preloader style={{ height: '30vh' }} />;

  if (!data) return null;

  const businessAccount = finances && finances[ACCOUNT_TYPES.business];
  const frozenAmount = businessAccount?.frozen_amount || 0;
  const showWarning =
    businessAccount && businessAccount.next_payment > businessAccount.cash + frozenAmount;

  return (
    <>
      {showWarning ? (
        <Alert
          type="danger"
          title={t('next_repayment.you_do_not_have_enough_funds')}
          description={t('next_repayment.make_sure_you_have_sufficient_funds')}
          icon={<img src={IconDanger} alt="danger" width={24} height={24} />}
          sessionId="no_repyament_funds_alert"
        />
      ) : (
        <Alert
          type="warning"
          title={t('next_repayment.each_month_you_must_make_repayment')}
          description={t('next_repayment.make_sure_you_have_sufficient_funds')}
          icon={<img src={IconWarn} alt="warning" width={24} height={24} />}
          sessionId="make_repayment_alert"
        />
      )}

      <div className={s.wrapper}>
        <div className={s.summary}>
          <div className={s.summary_block}>
            <div className={s.summary_icon}>
              <img src={IconCalendar} alt="calendar" width={24} height={24} />
            </div>
            <div className={s.summary_content}>
              <div className={s.summary_value}>{prettyDate(data.deadline, 'MMMM DD, YYYY')}</div>
              <div className={s.summary_label}>{t('next_repayment.due_date')}</div>
            </div>
          </div>
          <div className={s.summary_block}>
            <div className={s.summary_icon}>
              <img src={IconCoins} alt="calendar" width={24} height={24} />
            </div>
            <div className={s.summary_content}>
              <div className={s.summary_value}>{prettyFloatMoney(data.payable_now)}</div>
              <div className={s.summary_label}>{t('next_repayment.amount_to_pay')}</div>
            </div>
          </div>
        </div>

        <div className={s.repayments}>
          <div className="f-body-medium">{t('next_repayment.loan_repayments')}</div>
          <Table repayment={data} />
        </div>

        {!!data.reporting_service_fee && (
          <div className={s.additional}>
            <div className="f-body-medium">{t('next_repayment.additional_services')}</div>
            <div className={s.info}>
              <div className={s.row}>
                <div className="f-small-accent">
                  {t('next_repayment.reprorting_service_fee', {
                    mode: 'Opaque',
                  })}
                </div>
                <div className="f-small-regular">
                  {prettyFloatMoney(data.reporting_service_fee)}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={s.total}>
          <div className="f-body-medium">{t('next_repayment.total')}</div>
          <div className={s.info}>
            <div className={s.row}>
              <div className="f-small-accent">{t('next_repayment.repayment')}</div>
              <div className="f-small-regular">{prettyFloatMoney(data.total_amount)}</div>
            </div>
            {!!data.refin_amount && (
              <div className={s.row}>
                <div className="f-small-accent">{t('next_repayment.refinanced')}</div>
                <div className="f-small-regular">-{prettyFloatMoney(data.refin_amount)}</div>
              </div>
            )}
            {data.total_amount !== data.payable_now && (
              <div className={s.row}>
                <div className="f-small-accent">{t('next_repayment.amount_to_pay')}</div>
                <div className="f-small-regular">
                  <span className="f-500">{prettyFloatMoney(data.payable_now)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
